import * as Sentry from '@sentry/react';
import type { Envelope } from '@/lib/sync/envelope';
import { bytesToUuid } from '@/lib/sync/envelope';
import {
  KIND_AGENT_RUN,
  KIND_AGENT_EVENT,
  KIND_AGENT_DONE,
} from '@/lib/sync/envelope-kinds';
import type { SyncTransport } from '@/lib/sync/sync-transport';
import type { ConversationRegistry } from './conversation-registry';
import {
  applyConversationDoneBadge,
  applyApprovalBadge,
} from './background-notification';

/**
 * Subscribe a registry to the shared SyncTransport firehose so liveness is
 * tracked for EVERY conversation regardless of which tab (if any) is mounted.
 *
 * This subscription is REGISTRY-OWNED — it survives any provider/tab unmount.
 * It is NOT the per-turn ReadableStream in AssistantTransport.openTurnStream
 * (that one dies on unmount). Only this consumer guarantees a background run's
 * status reaches 'done' after the user switches away.
 *
 * Returns an unsubscribe fn — call it only when tearing down the whole sync
 * connection (e.g. workspace teardown), NOT on tab switch.
 */
export function attachLivenessConsumer<T extends { dispose: () => void }>(
  syncTransport: SyncTransport,
  registry: ConversationRegistry<T>,
): () => void {
  const decoder = new TextDecoder();
  return syncTransport.onFrame((envelope: Envelope) => {
    // Only compute the UUID for agent-kind frames — sync/yjs frames arrive
    // at high frequency and discarding them early avoids the bytesToUuid cost
    // on the hot path.
    if (envelope.kind === KIND_AGENT_RUN) {
      const convId = bytesToUuid(envelope.corrId);
      registry.setStatus(convId, 'running');
      return;
    }

    if (envelope.kind === KIND_AGENT_DONE) {
      const convId = bytesToUuid(envelope.corrId);
      registry.setStatus(convId, 'done');
      return;
    }

    if (envelope.kind === KIND_AGENT_EVENT) {
      if (envelope.json.length === 0) return;
      let body: { type?: string; hasPendingApproval?: boolean };
      try {
        body = JSON.parse(decoder.decode(envelope.json));
      } catch (err) {
        const convId = bytesToUuid(envelope.corrId);
        console.error('[ConversationLivenessConsumer] malformed KIND_AGENT_EVENT body', err);
        Sentry.captureException(err, {
          extra: { context: 'ConversationLivenessConsumer.json_parse', convId },
        });
        return;
      }
      // Only turn-complete is a liveness transition. Streaming chunks render
      // into Chat.messages via the per-turn stream — they don't touch status.
      if (body.type === 'turn-complete') {
        const convId = bytesToUuid(envelope.corrId);
        // hasPendingApproval means the run parked on a tool-approval interrupt —
        // surface an approval badge and park the registry at needs-approval.
        // Otherwise the run completed cleanly — surface a done badge.
        if (body.hasPendingApproval === true) {
          registry.setStatus(convId, 'needs-approval');
          applyApprovalBadge(convId);
        } else {
          registry.setStatus(convId, 'done');
          applyConversationDoneBadge(convId);
        }
      }
      // A tool-approval-request parks the run until the user approves or rejects.
      // Set registry status to 'needs-approval' so the eviction guard is intentional
      // (not an accidental side-effect of 'running' also being exempt).
      if (body.type === 'tool-approval-request') {
        const convId = bytesToUuid(envelope.corrId);
        registry.setStatus(convId, 'needs-approval');
      }
      return;
    }
    // Other kinds: ignored.
  });
}
